// =============================================================================
// ui/state/useProAction.ts — "may this user DO this?", for a click handler.
// -----------------------------------------------------------------------------
// The action-level twin of `useFreeTierCap`, and the same shape on purpose: a
// hook that answers one question, returning a refusal sentence or null.
//
// ── 🚨 WHY IT CANNOT BE A PLAIN FUNCTION LIKE THE OLD CLIENT'S ──
//
// `requirePro(feature)` is a synchronous one-liner over module globals, so any
// handler can call it inline. Entitlement here lives behind a React hook with no
// context and no provider, so it can only be read DURING RENDER. A click handler
// therefore has to close over an answer computed above it — which is what this
// returns.
//
// ⚠ That difference is not cosmetic. It means the answer a handler acts on is
// the one from its last render, so a component that gates an action must not
// also be the one that could unmount before the click lands. Every current
// caller is a button inside the screen that owns it.
//
// ── 🚨 `pending` IS NOT THE SAME QUESTION HERE AS IT IS FOR A TAB ──
//
// A gated TAB must not draw while the read is in flight, because failing open
// would flash the real screen. A gated ACTION has no such frame — nothing is
// revealed until the click. But the click itself can land mid-read, and a
// refusal computed from an unknown read would refuse a PAYING customer.
//
// So `pending` is surfaced and the rule is: while pending, ALLOW. That is the
// same asymmetry `domain/entitlement.ts` argues at length — a free user with Pro
// for one click is a bounded leak; a paying customer refused at the moment they
// reach for what they bought is a support ticket and a refund.
//
// ⚠ `pending` is hard-wired false while `PAID_LAUNCH` is off, so today this is
// unreachable — stated because the next reader will otherwise assume it is dead.
// =============================================================================

import { useEntitlement } from './useEntitlement';
import { proActionBlocked, type ProAction } from '../../selectors/proAction';

export interface ProActionGate {
  /** The refusal sentence, or null when the action may proceed. */
  blocked: string | null;
  /** True while the entitlement read is still in flight. See the header. */
  pending: boolean;
}

export function useProAction(action: ProAction): ProActionGate {
  const { pro, pending } = useEntitlement();
  return {
    // ⚠ `pro || pending` — see the header. Not `pro && !pending`, which would
    // refuse during the read and is the direction that costs a customer.
    blocked: proActionBlocked({ action, pro: pro || pending }),
    pending,
  };
}
